import { FormControl, FormErrorMessage, SystemStyleObject, Textarea } from '@chakra-ui/core';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';
import isMatch from 'lodash/isMatch';

import colors from '../../../../../../../styles/colors';
// eslint-disable-next-line import/no-cycle
import { QuestionnaireTemplateStore } from '../../../../store/questionnaireTemplateModel';
// eslint-disable-next-line import/no-cycle
import { useUpdateOptionValue } from '../../../hooks/useUpdateOptionValue';

const FORM_CONTROL_PROPS: { ml: number; display: 'flex'; flexDirection: 'column' } = {
  ml: 0,
  display: 'flex',
  flexDirection: 'column',
};

// const sanitize = (raw: string): string => raw.replace(/[\r\n]+/g, ' ').slice(0, OPTIONS_TEXT_MAX_LENGTH);

// const spliceAndSanitize = (base: string, inserted: string, selStart: number, selEnd: number): string =>
//   sanitize(base.slice(0, selStart) + inserted + base.slice(selEnd));

function buildInputStyles(hasError: boolean): SystemStyleObject {
  return {
    scrollbarColor: 'rgba(0, 0, 255, 0.3) rgba(0, 0, 255, 0.07)',
    scrollbarWidth: 'thin',
    resize: 'none',
    minHeight: 'unset',
    overflowY: 'auto',
    overflowX: 'hidden',
    bg: hasError ? 'red.50' : colors.gray[250],
    borderRadius: 'md',
    paddingX: 3,
    py: 0,
    border: '2px solid transparent',
    borderColor: hasError ? 'red.300' : 'transparent',
    _focus: {
      borderColor: hasError ? 'red.300' : colors.blue[400],
      bg: hasError ? 'red.50' : colors.gray[150],
    },
  };
}

const INPUT_STYLES_DEFAULT: SystemStyleObject = buildInputStyles(false);
const INPUT_STYLES_ERROR: SystemStyleObject = buildInputStyles(true);

function buildErrorKey(elementId: string, optionId: string): string {
  return `${elementId}.options.${optionId}.text`;
}

interface OptionInputProps {
  elementId: string;
  optionId: string;
  value: string;
}

function arePropsEqual(prev: OptionInputProps, next: OptionInputProps): boolean {
  return isMatch(prev, next);
}

export const OptionInput = React.memo(function OptionInput({ elementId, optionId, value }: OptionInputProps) {
  const { t } = useTranslation('questionnaireTemplate');

  const hasInteractedRef = useRef(false);

  const clearFieldError = QuestionnaireTemplateStore.useStoreActions((a) => a.clearFieldError);
  const updateOptionValue = useUpdateOptionValue();

  const errorKey = buildErrorKey(elementId, optionId);
  const error = QuestionnaireTemplateStore.useStoreState((s) => s.errors[errorKey]);

  useEffect(() => {
    clearFieldError(errorKey);
    return () => {
      clearFieldError(errorKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      hasInteractedRef.current = true;
      updateOptionValue({
        elementId,
        optionId,
        value: e.target.value,
        hasInteracted: true,
      });
    },
    [elementId, optionId, updateOptionValue],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  }, []);

  // const handlePaste = useCallback(
  //   (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
  //     const pasted = e.clipboardData.getData('text');
  //     const current = e.currentTarget.value;
  //     const selStart = e.currentTarget.selectionStart ?? current.length;
  //     const selEnd = e.currentTarget.selectionEnd ?? current.length;

  //     e.preventDefault();

  //     updateOptionValue({
  //       elementId,
  //       optionId,
  //       value: spliceAndSanitize(current, pasted, selStart, selEnd),
  //       hasInteracted: hasInteractedRef.current,
  //     });
  //   },
  //   [elementId, optionId, updateOptionValue],
  // );

  const hasError = Boolean(error);
  const inputStyles = hasError ? INPUT_STYLES_ERROR : INPUT_STYLES_DEFAULT;

  return (
    <FormControl {...FORM_CONTROL_PROPS} isInvalid={hasError}>
      <Textarea
        as={TextareaAutosize}
        minRows={1}
        maxRows={6}
        variant="unstyled"
        sx={inputStyles}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        // onPaste={handlePaste}
        placeholder={t('formBuilder.canvas.element.options.optionTextPlaceholder')}
        isInvalid={hasError}
      />
      {hasError && error !== undefined && (
        <FormErrorMessage fontSize="xs" mt={1}>
          {t(error.message, error.context)}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}, arePropsEqual);

OptionInput.displayName = 'OptionInput';
